import React, {
    ChangeEvent,
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    Plus,
    Trash2,
    Eye,
    Image as ImageIcon,
    FileText,
    Pencil,
    Search,
    ChevronDown,
    ChevronUp,
    Sparkles,
    CheckCircle2,
    UploadCloud,
    X,
    ExternalLink,
    BookOpen,
    Tag,
    Globe2
} from "lucide-react";

import AdminLayout from "../components/Layout/AdminLayout";

// const API_URL = "http://localhost:8483";
const API_URL = "https://www.ayushvatika.com";

interface Blog {
    id: number;
    title: string;
    slug: string;
    category?: string;
    excerpt?: string;
    featured_image?: string;
    status: "draft" | "published";
    published_at?: string;
    created_at: string;
    content?: string;
    image_alt?: string;
    seo_title?: string;
    meta_description?: string;
    focus_keyword?: string;
    short_tail_keywords?: string;
    long_tail_keywords?: string;
    author?: string;
}

type FormState = {
    title: string;
    slug: string;
    category: string;
    excerpt: string;
    content: string;
    image_alt: string;
    seo_title: string;
    meta_description: string;
    focus_keyword: string;
    short_tail_keywords: string;
    long_tail_keywords: string;
    author: string;
    status: "draft" | "published";
};

const INITIAL_FORM: FormState = {
    title: "",
    slug: "",
    category: "Ayurveda",
    excerpt: "",
    content: "",
    image_alt: "",
    seo_title: "",
    meta_description: "",
    focus_keyword: "",
    short_tail_keywords: "",
    long_tail_keywords: "",
    author: "Ayushvatika",
    status: "published"
};

const categories = [
    "Ayurveda",
    "Ayurvedic Herbs",
    "Ayurvedic Lifestyle",
    "Herbal Wellness",
    "Health & Wellness",
    "Self Care",
    "Wellness Tips"
];

const makeSlug = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

const getKeywords = (title: string, category: string) => {
    const clean = title
        .replace(/[^\w\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const main = clean || "Ayurveda";

    return {
        focus: main.toLowerCase(),
        short: `${main}, Ayurveda, ${category}, Ayurvedic wellness`,
        long: `${main} benefits and uses, how to use ${main} naturally, ${main} in Ayurveda, Ayurvedic ${category.toLowerCase()} tips`
    };
};

const AdminBlogs: React.FC = () => {
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [showSeo, setShowSeo] = useState(false);
    const [showHelp, setShowHelp] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const [form, setForm] = useState<FormState>(INITIAL_FORM);

    const fileRef = useRef<HTMLInputElement | null>(null);
    const contentRef = useRef<HTMLTextAreaElement | null>(null);

    const fetchBlogs = async () => {
        try {
            setFetching(true);

            const response = await fetch(
                `${API_URL}/api/blogs/admin/all`
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message || "Unable to load blogs."
                );
            }

            setBlogs(data.blogs || []);
        } catch (error) {
            console.error("Blogs fetch error:", error);
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchBlogs();
    }, []);

    const publishedCount = blogs.filter(
        (blog) => blog.status === "published"
    ).length;

    const draftCount = blogs.filter(
        (blog) => blog.status === "draft"
    ).length;

    const filteredBlogs = useMemo(() => {
        const query = searchTerm.toLowerCase().trim();

        if (!query) return blogs;

        return blogs.filter((blog) =>
            `${blog.title} ${blog.category || ""} ${blog.slug}`
                .toLowerCase()
                .includes(query)
        );
    }, [blogs, searchTerm]);

    const updateField = (
        name: keyof FormState,
        value: string
    ) => {
        setForm((previous) => ({
            ...previous,
            [name]: value
        }));
    };

    const handleTitleChange = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const title = e.target.value;

        setForm((previous) => {
            const next = {
                ...previous,
                title,
                slug: makeSlug(title)
            };

            if (!previous.image_alt || previous.image_alt === previous.title) {
                next.image_alt = title;
            }

            if (!previous.seo_title || previous.seo_title === previous.title) {
                next.seo_title = title;
            }

            if (!previous.focus_keyword) {
                next.focus_keyword = getKeywords(
                    title,
                    previous.category
                ).focus;
            }

            return next;
        });
    };

    const handleCategoryChange = (
        e: ChangeEvent<HTMLSelectElement>
    ) => {
        const category = e.target.value;

        setForm((previous) => {
            const keywords = getKeywords(
                previous.title,
                category
            );

            return {
                ...previous,
                category,
                short_tail_keywords:
                    previous.short_tail_keywords || keywords.short,
                long_tail_keywords:
                    previous.long_tail_keywords || keywords.long
            };
        });
    };

    const handleImageChange = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0];

        if (!file) return;

        const allowed = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp"
        ];

        if (!allowed.includes(file.type)) {
            alert("Please select a JPG, PNG or WebP image.");
            e.target.value = "";
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            alert("Image size must be less than 10MB.");
            e.target.value = "";
            return;
        }

        setSelectedFile(file);
        setImagePreview(URL.createObjectURL(file));

        if (!form.image_alt) {
            setForm((previous) => ({
                ...previous,
                image_alt:
                    previous.title ||
                    "Ayushvatika Ayurvedic herbs and wellness"
            }));
        }
    };

    const clearImage = () => {
        setSelectedFile(null);
        setImagePreview(null);

        if (fileRef.current) {
            fileRef.current.value = "";
        }
    };

    const generateSeo = () => {
        const title =
            form.title.trim() || "Ayurvedic Wellness";

        const keywords = getKeywords(
            title,
            form.category
        );

        const excerpt =
            form.excerpt.trim() ||
            `Learn about ${title}, its Ayurvedic benefits, traditional uses and practical wellness tips from Ayushvatika.`;

        setForm((previous) => ({
            ...previous,
            seo_title:
                `${title} | Ayurvedic Guide & Wellness Tips`.slice(
                    0,
                    255
                ),
            meta_description:
                excerpt.slice(0, 160),
            focus_keyword: keywords.focus,
            short_tail_keywords: keywords.short,
            long_tail_keywords: keywords.long,
            image_alt:
                previous.image_alt ||
                `${title} - Ayurvedic wellness`
        }));

        setShowSeo(true);
    };

    const insertAroundSelection = (
        before: string,
        after: string
    ) => {
        const textarea = contentRef.current;

        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = form.content.slice(start, end);

        const replacement =
            `${before}${selected || "Write here"}${after}`;

        const nextContent =
            form.content.slice(0, start) +
            replacement +
            form.content.slice(end);

        updateField("content", nextContent);

        requestAnimationFrame(() => {
            textarea.focus();

            const cursor =
                start +
                replacement.length;

            textarea.setSelectionRange(
                cursor,
                cursor
            );
        });
    };

    const handleSubmit = async (
        e: FormEvent
    ) => {
        e.preventDefault();

        if (!form.title.trim()) {
            alert("Please enter a blog title.");
            return;
        }

        if (!form.excerpt.trim()) {
            alert("Please add a short description.");
            return;
        }

        if (!form.content.trim()) {
            alert("Please write the blog content.");
            return;
        }

        if (!editingId && !selectedFile) {
            alert("Please select a featured image.");
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();

            Object.entries(form).forEach(
                ([key, value]) => {
                    formData.append(
                        key,
                        value
                    );
                }
            );

            if (selectedFile) {
                formData.append("featured_image", selectedFile);
            }

            const response = await fetch(
                editingId
                    ? `${API_URL}/api/blogs/admin/${editingId}`
                    : `${API_URL}/api/blogs/admin`,
                {
                    method: editingId ? "PUT" : "POST",
                    body: formData
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message ||
                    (editingId ? "Failed to update blog." : "Failed to create blog.")
                );
            }

            alert(
                editingId
                    ? "Blog updated successfully!"
                    : form.status === "published"
                        ? "Blog published successfully!"
                        : "Blog saved as draft!"
            );

            setEditingId(null);
            setForm(INITIAL_FORM);
            clearImage();
            setShowForm(false);
            setShowSeo(false);

            await fetchBlogs();
        } catch (error: any) {
            console.error("Blog submit error:", error);
            alert(
                error.message ||
                "Server error. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const deleteBlog = async (id: number) => {
        const confirmed = window.confirm(
            "Delete this blog permanently?"
        );

        if (!confirmed) return;

        try {
            const response = await fetch(
                `${API_URL}/api/blogs/admin/${id}`,
                {
                    method: "DELETE"
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message ||
                    "Delete failed."
                );
            }

            await fetchBlogs();
        } catch (error: any) {
            alert(
                error.message ||
                "Server error."
            );
        }
    };

    const startEdit = (blog: Blog) => {
        setEditingId(blog.id);
        setForm({
            title: blog.title || "",
            slug: blog.slug || "",
            category: blog.category || "Ayurveda",
            excerpt: blog.excerpt || "",
            content: blog.content || "",
            image_alt: blog.image_alt || "",
            seo_title: blog.seo_title || "",
            meta_description: blog.meta_description || "",
            focus_keyword: blog.focus_keyword || "",
            short_tail_keywords: blog.short_tail_keywords || "",
            long_tail_keywords: blog.long_tail_keywords || "",
            author: blog.author || "Ayushvatika",
            status: blog.status || "draft"
        });
        setSelectedFile(null);
        setImagePreview(blog.featured_image
            ? (blog.featured_image.startsWith("http")
                ? blog.featured_image
                : `${API_URL}${blog.featured_image}`)
            : null);
        setShowSeo(true);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const closeForm = () => {
        if (loading) return;

        setShowForm(false);
        setEditingId(null);
        setForm(INITIAL_FORM);
        clearImage();
        setShowSeo(false);
    };

    return (
        <AdminLayout>
            <div className="min-h-screen bg-[#f6f8f7] p-4 md:p-6 lg:p-8">

                {/* HEADER */}
                <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-green-700">
                            <BookOpen className="h-4 w-4" />
                            CONTENT MANAGEMENT
                        </div>

                        <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                            Ayurveda Blogs
                        </h1>

                        <p className="mt-1 text-sm text-slate-500">
                            Create helpful Ayurveda articles and publish them on your website.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowForm(true)}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-green-800"
                    >
                        <Plus className="h-5 w-5" />
                        Create New Blog
                    </button>
                </div>

                {/* STATS */}
                <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                        <p className="text-sm text-slate-500">Total Blogs</p>
                        <p className="mt-1 text-3xl font-bold text-slate-900">
                            {blogs.length}
                        </p>
                    </div>

                    <div className="rounded-2xl border border-green-100 bg-green-50 p-5 shadow-sm">
                        <p className="text-sm text-green-700">Published</p>
                        <p className="mt-1 text-3xl font-bold text-green-800">
                            {publishedCount}
                        </p>
                    </div>

                    <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5 shadow-sm">
                        <p className="text-sm text-amber-700">Drafts</p>
                        <p className="mt-1 text-3xl font-bold text-amber-800">
                            {draftCount}
                        </p>
                    </div>
                </div>

                {/* SIMPLE HELP */}
                {showHelp && !showForm && (
                    <div className="mb-7 rounded-2xl border border-green-100 bg-gradient-to-r from-green-50 to-white p-5">
                        <div className="flex gap-4">
                            <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green-100 text-green-700 sm:flex">
                                <Sparkles className="h-5 w-5" />
                            </div>

                            <div className="flex-1">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <h3 className="font-bold text-slate-900">
                                            Blogging is easy here
                                        </h3>
                                        <p className="mt-1 text-sm leading-6 text-slate-600">
                                            Just add a title, image and article. The URL and basic SEO fields can be prepared automatically for you.
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => setShowHelp(false)}
                                        className="rounded-lg p-1 text-slate-400 hover:bg-white hover:text-slate-700"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium text-slate-600">
                                    {[
                                        "1. Write",
                                        "2. Add image",
                                        "3. Generate SEO",
                                        "4. Publish"
                                    ].map((item) => (
                                        <span
                                            key={item}
                                            className="rounded-full bg-white px-3 py-1.5 ring-1 ring-slate-200"
                                        >
                                            <CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-green-600" />
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* CREATE FORM */}
                {showForm && (
                    <form
                        onSubmit={handleSubmit}
                        className="mb-8 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
                    >
                        {/* FORM HEADER */}
                        <div className="border-b border-slate-100 bg-gradient-to-r from-green-50 via-white to-white px-5 py-6 md:px-7">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex gap-3">
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-100 text-green-700">
                                        <FileText className="h-5 w-5" />
                                    </div>

                                    <div>
                                        <h2 className="text-xl font-bold text-slate-900">
                                            {editingId ? "Edit Blog" : "Create New Blog"}
                                        </h2>
                                        <p className="mt-1 text-sm text-slate-500">
                                            Fill the simple details below. Advanced SEO is optional.
                                        </p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={closeForm}
                                    className="rounded-xl p-2 text-slate-400 hover:bg-white hover:text-slate-700"
                                >
                                    <X />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-8 p-5 md:p-7">

                            {/* STEP 1 */}
                            <section>
                                <SectionTitle
                                    number="01"
                                    title="Blog basics"
                                    description="Start with the title and a short description."
                                />

                                <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                                    <div className="lg:col-span-2">
                                        <FieldLabel
                                            label="Blog title"
                                            required
                                            help="This is the main heading readers will see."
                                        />

                                        <input
                                            value={form.title}
                                            onChange={handleTitleChange}
                                            required
                                            placeholder="Example: 7 Benefits of Ashwagandha in Ayurveda"
                                            className="admin-input w-full rounded-xl border border-slate-200 px-4 py-3.5 outline-none transition focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                        />
                                    </div>

                                    <div>
                                        <FieldLabel
                                            label="Category"
                                            required
                                        />

                                        <select
                                            value={form.category}
                                            onChange={handleCategoryChange}
                                            className="admin-input w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                        >
                                            {categories.map((category) => (
                                                <option
                                                    key={category}
                                                    value={category}
                                                >
                                                    {category}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="lg:col-span-3">
                                        <FieldLabel
                                            label="Short description"
                                            required
                                            help="A 1–2 sentence summary shown on blog cards and useful for search engines."
                                        />

                                        <textarea
                                            value={form.excerpt}
                                            onChange={(e) =>
                                                updateField(
                                                    "excerpt",
                                                    e.target.value
                                                )
                                            }
                                            required
                                            rows={3}
                                            maxLength={320}
                                            placeholder="Example: Discover the traditional Ayurvedic benefits, uses and wellness tips related to Ashwagandha."
                                            className="admin-input w-full resize-none rounded-xl border border-slate-200 px-4 py-3.5 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                        />

                                        <p className="mt-1 text-right text-xs text-slate-400">
                                            {form.excerpt.length}/320
                                        </p>
                                    </div>
                                </div>
                            </section>

                            {/* STEP 2 IMAGE */}
                            <section>
                                <SectionTitle
                                    number="02"
                                    title="Featured image"
                                    description="Upload one good image. The backend automatically converts it to WebP and crops it to the website's standard shape."
                                />

                                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
                                    <div>
                                        <button
                                            type="button"
                                            onClick={() => fileRef.current?.click()}
                                            className="group flex min-h-[190px] w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-5 text-center transition hover:border-green-600 hover:bg-green-50"
                                        >
                                            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white text-green-700 shadow-sm ring-1 ring-slate-200">
                                                <UploadCloud className="h-6 w-6" />
                                            </div>

                                            <p className="font-semibold text-slate-800">
                                                Click to upload image
                                            </p>

                                            <p className="mt-1 text-sm text-slate-500">
                                                JPG, PNG or WebP · Maximum 10MB
                                            </p>

                                            <p className="mt-3 text-xs text-green-700">
                                                Recommended: clear herbs / Ayurveda / wellness image
                                            </p>
                                        </button>

                                        <input
                                            ref={fileRef}
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            onChange={handleImageChange}
                                            className="hidden"
                                        />
                                    </div>

                                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                                        {imagePreview ? (
                                            <>
                                                <div className="aspect-[3/2] w-full overflow-hidden">
                                                    <img
                                                        src={imagePreview}
                                                        alt="Selected preview"
                                                        className="h-full w-full object-cover"
                                                    />
                                                </div>

                                                <div className="flex items-center justify-between gap-3 p-3">
                                                    <div className="min-w-0">
                                                        <p className="truncate text-sm font-semibold text-slate-800">
                                                            {selectedFile?.name || (editingId ? "Current featured image" : "Selected image")}
                                                        </p>
                                                        <p className="text-xs text-slate-500">
                                                            {selectedFile ? "Ready to upload" : "Existing image will be kept unless you choose another"}
                                                        </p>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={clearImage}
                                                        className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex aspect-[3/2] items-center justify-center">
                                                <div className="text-center text-slate-400">
                                                    <ImageIcon className="mx-auto mb-2 h-8 w-8" />
                                                    <p className="text-sm">
                                                        Image preview
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-5">
                                    <FieldLabel
                                        label="Image description"
                                        required
                                        help="Describe the image naturally. This is used as image alt text for accessibility and SEO."
                                    />

                                    <input
                                        value={form.image_alt}
                                        onChange={(e) =>
                                            updateField(
                                                "image_alt",
                                                e.target.value
                                            )
                                        }
                                        required
                                        placeholder="Example: Ashwagandha herb used in traditional Ayurveda"
                                        className="w-full rounded-xl border border-slate-200 px-4 py-3.5 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                    />
                                </div>
                            </section>

                            {/* STEP 3 CONTENT */}
                            <section>
                                <SectionTitle
                                    number="03"
                                    title="Write your article"
                                    description="Write normally. You do not need to understand HTML. The small buttons below help with common formatting."
                                />

                                <div className="overflow-hidden rounded-2xl border border-slate-200">
                                    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 p-3">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                insertAroundSelection(
                                                    "<h2>",
                                                    "</h2>"
                                                )
                                            }
                                            className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-green-50 hover:text-green-700"
                                        >
                                            Heading
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                insertAroundSelection(
                                                    "<h3>",
                                                    "</h3>"
                                                )
                                            }
                                            className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-green-50 hover:text-green-700"
                                        >
                                            Subheading
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                insertAroundSelection(
                                                    "<p>",
                                                    "</p>"
                                                )
                                            }
                                            className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-green-50 hover:text-green-700"
                                        >
                                            Paragraph
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                insertAroundSelection(
                                                    "<strong>",
                                                    "</strong>"
                                                )
                                            }
                                            className="rounded-lg bg-white px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 hover:bg-green-50 hover:text-green-700"
                                        >
                                            Bold
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                insertAroundSelection(
                                                    "<ul><li>",
                                                    "</li></ul>"
                                                )
                                            }
                                            className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-green-50 hover:text-green-700"
                                        >
                                            List
                                        </button>
                                    </div>

                                    <textarea
                                        ref={contentRef}
                                        value={form.content}
                                        onChange={(e) =>
                                            updateField(
                                                "content",
                                                e.target.value
                                            )
                                        }
                                        required
                                        rows={18}
                                        placeholder={`Write your article here...

Example:

Start with a short introduction.

Then explain the topic in simple language.

Use the Heading and Subheading buttons above when you want to structure the article.

Try to answer the reader's questions naturally and avoid keyword stuffing.`}
                                        className="w-full resize-y border-0 px-4 py-4 text-[15px] leading-7 outline-none focus:ring-0"
                                    />
                                </div>

                                <div className="mt-2 flex flex-col gap-1 text-xs text-slate-400 sm:flex-row sm:justify-between">
                                    <span>
                                        Tip: useful, original and easy-to-read content is better than repeating keywords.
                                    </span>
                                    <span>
                                        {form.content.length} characters
                                    </span>
                                </div>
                            </section>

                            {/* STEP 4 SEO */}
                            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowSeo(!showSeo)
                                    }
                                    className="flex w-full items-center justify-between gap-4 p-5 text-left"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100 text-green-700">
                                            <Search className="h-5 w-5" />
                                        </div>

                                        <div>
                                            <h3 className="font-bold text-slate-900">
                                                SEO settings
                                                <span className="ml-2 rounded-full bg-green-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-green-700">
                                                    Optional
                                                </span>
                                            </h3>

                                            <p className="mt-1 text-sm text-slate-500">
                                                You can let Ayushvatika prepare these fields automatically.
                                            </p>
                                        </div>
                                    </div>

                                    {showSeo ? (
                                        <ChevronUp />
                                    ) : (
                                        <ChevronDown />
                                    )}
                                </button>

                                {showSeo && (
                                    <div className="border-t border-slate-200 bg-white p-5 md:p-6">
                                        <div className="mb-5 flex flex-col gap-3 rounded-xl bg-green-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                                            <div>
                                                <p className="font-semibold text-green-900">
                                                    Don't know SEO?
                                                </p>
                                                <p className="text-sm text-green-800">
                                                    Click the button and we'll prepare the basic SEO fields from your title.
                                                </p>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={generateSeo}
                                                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-800"
                                            >
                                                <Sparkles className="h-4 w-4" />
                                                Generate SEO
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                                            <div>
                                                <FieldLabel
                                                    label="SEO title"
                                                    help="A search-friendly title. Keep it clear and natural."
                                                />

                                                <input
                                                    value={form.seo_title}
                                                    onChange={(e) =>
                                                        updateField(
                                                            "seo_title",
                                                            e.target.value
                                                        )
                                                    }
                                                    maxLength={255}
                                                    placeholder="Example: Ashwagandha Benefits | Ayurvedic Wellness Guide"
                                                    className="w-full rounded-xl border border-slate-200 px-4 py-3.5 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                                />

                                                <p className="mt-1 text-right text-xs text-slate-400">
                                                    {form.seo_title.length}/255
                                                </p>
                                            </div>

                                            <div>
                                                <FieldLabel
                                                    label="Main keyword"
                                                    help="The main phrase this article is about."
                                                />

                                                <input
                                                    value={form.focus_keyword}
                                                    onChange={(e) =>
                                                        updateField(
                                                            "focus_keyword",
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Example: ashwagandha benefits"
                                                    className="w-full rounded-xl border border-slate-200 px-4 py-3.5 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                                />
                                            </div>

                                            <div className="lg:col-span-2">
                                                <FieldLabel
                                                    label="Meta description"
                                                    help="A short summary that can appear in Google search results."
                                                />

                                                <textarea
                                                    value={form.meta_description}
                                                    onChange={(e) =>
                                                        updateField(
                                                            "meta_description",
                                                            e.target.value
                                                        )
                                                    }
                                                    maxLength={320}
                                                    rows={3}
                                                    placeholder="Write a natural 150–160 character summary..."
                                                    className="w-full resize-none rounded-xl border border-slate-200 px-4 py-3.5 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                                />

                                                <p className="mt-1 text-right text-xs text-slate-400">
                                                    {form.meta_description.length}/320
                                                </p>
                                            </div>

                                            <div>
                                                <FieldLabel
                                                    label="Short keywords"
                                                    help="Short search phrases separated by commas."
                                                />

                                                <textarea
                                                    value={form.short_tail_keywords}
                                                    onChange={(e) =>
                                                        updateField(
                                                            "short_tail_keywords",
                                                            e.target.value
                                                        )
                                                    }
                                                    rows={4}
                                                    placeholder="Ayurveda, Ayurvedic herbs, wellness"
                                                    className="w-full resize-none rounded-xl border border-slate-200 px-4 py-3.5 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                                />
                                            </div>

                                            <div>
                                                <FieldLabel
                                                    label="Long-tail keywords"
                                                    help="Specific phrases people may search for."
                                                />

                                                <textarea
                                                    value={form.long_tail_keywords}
                                                    onChange={(e) =>
                                                        updateField(
                                                            "long_tail_keywords",
                                                            e.target.value
                                                        )
                                                    }
                                                    rows={4}
                                                    placeholder="benefits of ashwagandha in Ayurveda, how to use ashwagandha naturally"
                                                    className="w-full resize-none rounded-xl border border-slate-200 px-4 py-3.5 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </section>

                            {/* URL / PUBLISH */}
                            <section className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                    <div className="mb-3 flex items-center gap-2">
                                        <Globe2 className="h-5 w-5 text-green-700" />
                                        <h3 className="font-bold text-slate-900">
                                            Website URL
                                        </h3>
                                    </div>

                                    <p className="mb-2 text-xs text-slate-500">
                                        This is generated automatically from your title.
                                    </p>

                                    <div className="rounded-xl bg-white px-4 py-3 text-sm text-slate-600 ring-1 ring-slate-200">
                                        <span className="text-slate-400">
                                            /blogs/
                                        </span>
                                        <span className="font-medium text-slate-800">
                                            {form.slug || "your-blog-url"}
                                        </span>
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                    <div className="mb-3 flex items-center gap-2">
                                        <Tag className="h-5 w-5 text-green-700" />
                                        <h3 className="font-bold text-slate-900">
                                            Publishing
                                        </h3>
                                    </div>

                                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                                        What should happen after saving?
                                    </label>

                                    <select
                                        value={form.status}
                                        onChange={(e) =>
                                            updateField(
                                                "status",
                                                e.target.value
                                            )
                                        }
                                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                                    >
                                        <option value="published">
                                            Publish on website
                                        </option>

                                        <option value="draft">
                                            Save as draft
                                        </option>
                                    </select>
                                </div>
                            </section>

                            {/* ACTIONS */}
                            <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-end">
                                <button
                                    type="button"
                                    onClick={closeForm}
                                    disabled={loading}
                                    className="rounded-xl border border-slate-200 bg-white px-6 py-3 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-7 py-3 font-semibold text-white shadow-sm hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {loading ? (
                                        <>
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 className="h-5 w-5" />
                                            {editingId ? "Update Blog" : form.status === "published" ? "Publish Blog" : "Save Draft"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                )}

                {/* BLOG LIST */}
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-col gap-4 border-b border-slate-100 p-5 md:flex-row md:items-center md:justify-between md:p-6">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">
                                All Blogs
                            </h2>
                            <p className="mt-1 text-sm text-slate-500">
                                Latest blogs appear first on your website.
                            </p>
                        </div>

                        <div className="relative w-full md:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                            <input
                                value={searchTerm}
                                onChange={(e) =>
                                    setSearchTerm(e.target.value)
                                }
                                placeholder="Search blogs..."
                                className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                            />
                        </div>
                    </div>

                    {fetching ? (
                        <div className="p-12 text-center text-sm text-slate-500">
                            Loading blogs...
                        </div>
                    ) : filteredBlogs.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                                <FileText />
                            </div>

                            <h3 className="mt-4 font-semibold text-slate-800">
                                No blogs found
                            </h3>

                            <p className="mt-1 text-sm text-slate-500">
                                {searchTerm
                                    ? "Try another search."
                                    : "Create your first Ayurveda blog to get started."}
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {filteredBlogs.map((blog) => (
                                <div
                                    key={blog.id}
                                    className="flex flex-col gap-4 p-4 transition hover:bg-slate-50 md:flex-row md:items-center md:p-5"
                                >
                                    <div className="h-24 w-full shrink-0 overflow-hidden rounded-xl bg-slate-100 md:w-36">
                                        {blog.featured_image ? (
                                            <img
                                                src={`${API_URL}${blog.featured_image}`}
                                                alt={blog.title}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-full items-center justify-center text-slate-400">
                                                <ImageIcon />
                                            </div>
                                        )}
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="mb-1 flex flex-wrap items-center gap-2">
                                            <span className="rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700">
                                                {blog.category || "Ayurveda"}
                                            </span>

                                            <span
                                                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                                    blog.status === "published"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-amber-100 text-amber-700"
                                                }`}
                                            >
                                                {blog.status === "published"
                                                    ? "Published"
                                                    : "Draft"}
                                            </span>
                                        </div>

                                        <h3 className="truncate text-base font-bold text-slate-900 md:text-lg">
                                            {blog.title}
                                        </h3>

                                        <p className="mt-1 truncate text-xs text-slate-400">
                                            /blogs/{blog.slug}
                                        </p>

                                        <p className="mt-2 text-xs text-slate-500">
                                            {blog.published_at
                                                ? new Date(
                                                      blog.published_at
                                                  ).toLocaleDateString(
                                                      "en-IN"
                                                  )
                                                : "Not published yet"}
                                        </p>
                                    </div>

                                    <div className="flex shrink-0 items-center gap-2">
                                        {blog.status === "published" && (
                                            <a
                                                href={`/blogs/${blog.slug}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                                            >
                                                <Eye className="h-4 w-4" />
                                                <span className="hidden sm:inline">
                                                    View
                                                </span>
                                            </a>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => startEdit(blog)}
                                            className="rounded-xl bg-blue-50 p-2.5 text-blue-600 hover:bg-blue-100"
                                            title="Edit blog"
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                deleteBlog(blog.id)
                                            }
                                            className="rounded-xl bg-red-50 p-2.5 text-red-600 hover:bg-red-100"
                                            title="Delete"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

const SectionTitle = ({
    number,
    title,
    description
}: {
    number: string;
    title: string;
    description: string;
}) => (
    <div className="mb-5 flex gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-700 text-xs font-bold text-white">
            {number}
        </div>

        <div>
            <h3 className="font-bold text-slate-900">
                {title}
            </h3>

            <p className="mt-1 text-sm leading-6 text-slate-500">
                {description}
            </p>
        </div>
    </div>
);

const FieldLabel = ({
    label,
    required,
    help
}: {
    label: string;
    required?: boolean;
    help?: string;
}) => (
    <div className="mb-2">
        <label className="block text-sm font-semibold text-slate-800">
            {label}
            {required && (
                <span className="ml-1 text-red-500">*</span>
            )}
        </label>

        {help && (
            <p className="mt-0.5 text-xs text-slate-400">
                {help}
            </p>
        )}
    </div>
);

export default AdminBlogs;
